import localforage from 'localforage';

import { hasKey, isObject } from '../utils/typeChecks';

type DBValue<T> = T | { value: T; dbExpiry: number };

export class ForageService {
  private static instance: ForageService;

  private constructor() {
    this.initDbStorage();
  }

  public static getInstance(): ForageService {
    if (!ForageService.instance) {
      ForageService.instance = new ForageService();
    }
    return ForageService.instance;
  }

  private initDbStorage(): void {
    const storeName = window.DEBUG ? 'staging' : 'production';
    localforage.config({
      storeName,
      name: window.BRAND,
      driver: [
        localforage.INDEXEDDB,
        localforage.WEBSQL,
        localforage.LOCALSTORAGE,
      ],
    });
  }

  public async getValue<T>(key: string): Promise<T | null> {
    try {
      const item = await localforage.getItem<DBValue<T>>(key);
      if (isObject(item)) {
        if (hasKey(item, 'dbExpiry')) {
          if (new Date().getTime() > item.dbExpiry) {
            this.deleteValue(key);
            return null;
          } else {
            return item.value;
          }
        }
      }
      return item;
    } catch (e) {
      Errors.report(e);
      return null;
    }
  }

  public async deleteValue(key: string): Promise<void> {
    try {
      await localforage.removeItem(key);
    } catch (e) {
      Errors.report(e);
    }
  }

  public async setValue(key: string, value: unknown): Promise<void> {
    try {
      await localforage.setItem(key, value);
    } catch (e) {
      Errors.report(e);
    }
  }

  public async setValueWithExpiry(
    key: string,
    value: unknown,
    expiryMin: number = 60,
  ): Promise<void> {
    try {
      const dbExpiry = new Date().getTime() + expiryMin * 60000; // Expiry time in milliseconds
      const valueWithExpiry = {
        value,
        dbExpiry,
      };
      await localforage.setItem(key, valueWithExpiry);
    } catch (e) {
      Errors.report(e);
    }
  }
}
